import React, {useCallback, useMemo, useState} from 'react';
import {
  DashboardLayout,
  Filter,
  FilterButton,
  Loader,
  PageLimit,
  Pagination,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  TableLayout,
} from '../../../components';
import {
  baasMobile,
  corruptedFile,
  eye,
  green,
  netFlow,
  red,
} from '../../../assets/images/images';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useQuery} from 'react-query';
import {
  fetchBAASTransactionPayouts,
  fetchBAASTransactionPayoutsSummary,
} from '../../../requests/queries/baas';
import moment from 'moment/moment';
import {shortenLargeNumbers} from '../../../helpers/shortenLargeNumbers';
import {getStatusTextAndStyles} from '../../../helpers/getStatusTextAndStyles';
import {filterOutEmptyValues, generateQueryParams} from '../../../helpers';
import BaasPayoutModal from './BaasPayoutModal';
import {useBaasExport} from './hooks/useBAASExport';
import BaasWithdrawModal from './BaasWithdrawModal';
import {useBaasSettlement} from './hooks/useBaasSettlement';
import {usePermissions} from '../../../hooks/useSidbarPermissions';
import {EmptyFilterState} from '../../../components/EmptyFilterState';
import AppliedFilters from 'components/AppliedFilters';
import CardSummary from 'components/CardSummary';
import {CreatePinModal} from './components/CreatePinModal';
import {toast} from 'react-toastify';

const initialFilters = {
  start: '',
  end: '',
  flows: '',
};

function BaasPayouts() {
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState(false);
  const [isWithdrawModal, setIsWithdrawModal] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [searchText, setSearchText] = useState('');
  const [transaction, setTransaction] = useState({});
  const [transactionDetails, setTransactionDetails] = useState(false);
  const {userDetails} = useSelector(state => state?.auth);
  const [createPin, setCreatePin] = useState(false);

  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });
  const {user_account} = useBaasSettlement();
  const {
    baasPagePermissions: {canExportPayouts},
  } = usePermissions();
  const [filterProps, setFilterProps] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const toggleWithdrawModal = () => setIsWithdrawModal(prevState => !prevState);

  const toggleTransactionsModal = useCallback(transaction => {
    setTransactionDetails(prevState => !prevState);
    setTransaction(transaction);
  }, []);

  const params = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      ...query,
      search: searchText,
      start_date: filters?.start,
      end_date: filters?.end,
    }),
    [filters?.end, filters?.start, query, searchText, userDetails],
  );

  const summaryParams = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      start_date: filters?.start,
      end_date: filters?.end,
    }),
    [userDetails, filters?.start, filters?.end],
  );

  const {handleExport, exportData} = useBaasExport({
    params,
    apiFunc: fetchBAASTransactionPayouts,
  });

  const {data: baas_payouts, isLoading} = useQuery(
    ['baas-payouts', {params}],
    () => fetchBAASTransactionPayouts({params}),
    {enabled: !exportData},
  );

  const {data: baas_payouts_summary, isLoading: summaryLoading} = useQuery(
    ['baas-payouts-summary', {summaryParams}],
    () => fetchBAASTransactionPayoutsSummary({params: summaryParams}),
    {enabled: !exportData},
  );

  const payouts = useMemo(
    () => !isLoading && baas_payouts && baas_payouts?.entity,
    [baas_payouts, isLoading],
  );

  const pageClick = selected => {
    setQuery({
      ...query,
      page: selected,
    });
  };

  const handleFilter = (selectedOptions, shouldDownload = false) => {
    const filteredOptions = filterOutEmptyValues(selectedOptions);
    const queryParams = generateQueryParams(filteredOptions);
    const {status} = filteredOptions;
    if (Array.isArray(status)) {
      filteredOptions.status = status.join(',');
    }
    let baseUrl = '';
    if (queryParams) {
      baseUrl += `?${queryParams}`;
    }

    if (shouldDownload) {
      filteredOptions.download = true;
      setFilters({
        ...filters,
        ...filteredOptions,
      });
    } else {
      navigate(baseUrl);
    }

    setFiltersApplied(true);
    setFilters(filteredOptions);
    setOpenFilter(false);
    setFiltersApplied(
      Object.values(filteredOptions).some(value => value) ||
        searchText.length > 0,
    );
  };

  const {
    baasPagePermissions: {canTransfer},
  } = usePermissions();

  const handleResetFilter = () => {
    navigate('');
    setFilters(initialFilters);
    setOpenFilter(true);
    setSearchText('');
    setFiltersApplied(false);
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(
      Object.values(filters).some(value => value) || searchText.length > 0,
    );
  };

  const summaryData = useMemo(
    () =>
      (!summaryLoading &&
        baas_payouts_summary &&
        baas_payouts_summary?.entity) ||
      {},
    [baas_payouts_summary, summaryLoading],
  );

  // const filterCount = Number(sessionStorage.getItem('filterCount') || 0);

  const isNotFiltered =
    !isLoading && (!payouts?.settlements || payouts?.settlements.length === 0);
  const isFiltered =
    isNotFiltered &&
    (Object.values(filters).some(s => s) ||
      (searchText && searchText.length > 0));

  const handleTransferClick = () => {
    if (user_account?.has_pin) {
      toggleWithdrawModal();
    } else {
      setCreatePin(true);
      toast.error('Please create a Transaction PIN before you proceed.');
    }
  };

  return (
    <DashboardLayout
      bg="bg-white80"
      topPadding="pt-8"
      xlLeftMargin="xl:ml-12"
      breadCrumbs={
        <div className="flex items-center">
          <img src={baasMobile} alt="" />
          <p className="ml-2 -mb-1">Payout</p>
        </div>
      }
    >
      <div className="flex items-center gap-4 flex-wrap">
        <p className="text-grey font-medium text-[18px]">Payout</p>
        {filtersApplied && filterProps && (
          <AppliedFilters {...filterProps} isMainPage={true} />
        )}
      </div>

      <div className="flex mt-8 items-start lg:items-center lg:flex-row flex-col gap-5 justify-between p-5 lg:p-0">
        <div className="flex items-center gap-2">
          <SearchInput
            onChange={value => setSearchText(value)}
            className="sm:w-[277px] rounded-lg outline-[#eceff3]"
          />{' '}
          <FilterButton
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            className="h-[48px] justify-center w-[130px]"
          />
        </div>
        <div className="flex items-center gap-2">
          <SecondaryButton
            buttonText="Export"
            className="w-[121px]"
            onClick={handleExport}
            loading={exportData}
            disabled={!canExportPayouts}
          />

          <PrimaryButton
            buttonText="Transfer"
            className="w-[121px]"
            onClick={handleTransferClick}
            disabled={!canTransfer}
          />
        </div>
      </div>

      <BaasWithdrawModal
        show={isWithdrawModal}
        onClose={toggleWithdrawModal}
        userBalance={user_account?.amount}
        type="transfer"
      />

      <CreatePinModal show={createPin} onClose={() => setCreatePin(false)} />

      <BaasPayoutModal
        show={transactionDetails}
        onClose={toggleTransactionsModal}
        transaction={transaction}
      />

      <Filter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        handleFilter={handleFilter}
        resetUrl={handleResetFilter}
        onFilterStateChange={handleFilterStateChange}
        filterOptions={[]}
      />

      <div className="mt-8">
        <>
          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            {/* <CardSummary
              img={green}
              title="Total Collections"
              body={summaryData?.deposit || '₦0'}
              className="!bg-white !px-6 !py-8"
              loading={summaryLoading}
            /> */}
            <CardSummary
              img={netFlow}
              title="Total Payout"
              body={summaryData?.transfer || '₦0'}
              className="!bg-white !px-6 !py-8"
              loading={summaryLoading}
            />
            {/* <CardSummary
              img={red}
              title="Estimated Settlements"
              body={summaryData?.settlement || '₦0'}
              className="!bg-white !px-6 !py-8"
              loading={summaryLoading}
            /> */}
          </div>
        </>

        <TableLayout negativeMargins className="mt-6 w-full">
          <thead className="text-small font-semibold uppercase">
            <tr className="bg-white80">
              <th className="p-5 pl-12 font-medium text-left text-xs text-grey whitespace-nowrap w-[23%]">
                date
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                recipient
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                amount
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                type
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
                status
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap"></th>
            </tr>
          </thead>

          {isLoading ? (
            <tbody className="bg-white">
              <tr>
                <td colSpan={6}>
                  <Loader height={35} />
                </td>
              </tr>
            </tbody>
          ) : !isLoading && payouts && payouts?.settlements?.length === 0 ? (
            isFiltered ? (
              <EmptyFilterState
                noBtn
                customBtn={
                  <button
                    onClick={() => {
                      navigate('');
                      setFilters(initialFilters);
                      setSearchText('');
                      setFiltersApplied(false);
                      setTimeout(() => {
                        setOpenFilter(true);
                      }, 100);
                    }}
                    className="text-brandBlue p-4 text-sm font-medium"
                  >
                    Update preferences
                  </button>
                }
                body="Oops, it looks like there are no payouts that match the filters/search term you've selected. Please try adjusting your filters."
                className="w-full"
                src={corruptedFile}
                maxWidth="max-w-[500px]"
              />
            ) : (
              <EmptyFilterState
                noBtn
                customBtn={<></>}
                body="Oops! No Payouts history found for this company"
                className="w-full"
                src={corruptedFile}
                maxWidth="max-w-[500px]"
              />
            )
          ) : (
            <tbody className="text-sm text-body bg-white">
              {!isLoading &&
                payouts &&
                payouts?.settlements?.map((settlement, idx) => (
                  <tr
                    key={idx}
                    className="text-left border-b  bg-white cursor-pointer border-grey60"
                    onClick={() => toggleTransactionsModal(settlement)}
                  >
                    <td className="p-4 pl-12 whitespace-nowrap w-[23%]">
                      {(settlement?.date_updated &&
                        moment(settlement?.date_updated).format(
                          'DD MMM YYYY h:m',
                        )) ||
                        '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {settlement?.account_name || '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {shortenLargeNumbers(
                        Number(settlement?.transaction_amount) || 0,
                        'NGN',
                        {
                          startAt: 'M',
                        },
                      )}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <span className="uppercase   w-fit rounded-full px-2 py-1 h-5 flex items-center justify-center text-dojahBlue bg-[#EEF3FC] text-xs">
                        manual
                      </span>
                    </td>

                    <td className="p-4 pl-0 whitespace-nowrap">
                      <span
                        className={` ${
                          getStatusTextAndStyles(
                            settlement?.transaction_status?.toLowerCase(),
                          )?.styles
                        } uppercase w-fit rounded-full px-2 py-1 h-5   flex items-center justify-center  text-xs`}
                      >
                        {
                          getStatusTextAndStyles(
                            settlement?.transaction_status?.toLowerCase(),
                          )?.text
                        }
                      </span>
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <button>
                        <img src={eye} alt="" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          )}
        </TableLayout>
      </div>

      {!isLoading && payouts?.settlements?.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-12 sm:gap-0 pl-12 pr-6">
          <PageLimit
            onLimitChange={length => {
              setQuery(prevState => ({
                ...prevState,
                length,
                page: 1,
              }));
            }}
            limit={query?.length}
            total={payouts?.current_page}
            length={payouts?.settlements?.length}
          />

          <Pagination
            total={payouts?.total_pages}
            current={Number(payouts?.current_page)}
            onPageChange={activePage => {
              pageClick(activePage);
              const searchParams = new URLSearchParams(
                document.location.search,
              );
              searchParams.set('page', activePage);
              const newSearchParams = searchParams.toString();
              navigate(`?${newSearchParams}`);
            }}
          />
        </div>
      )}
    </DashboardLayout>
  );
}

export default BaasPayouts;
